import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useAppConfigContext } from '@/context/AppConfig';
import { RawReportType } from '@/recentReports/Interfaces';
import { useReportRedirect } from '@/reports/index';
import { ReportSearchRequestBody, searchReport } from '@/reports/searchReport';
import { RapidsInput } from '@/search/index';
import { RapidsStateRef } from '@/search/rapids/RapidsInput';
import { RapidsSearchData } from '@/search/rapids/rapids';
import {
  IconButton,
  Input,
  InputAdornment,
  InputProps,
  Text,
} from '@/theme/components';
import { Icons, useTheme } from '@/theme/index';
import { isValueAPhoneNumber } from '@/utils/formatPhone';
import { ValidReportTypes } from '@/utils/helperTypes';
import { formatPhoneInput, unformatPhone } from '@/utils/index';
import { schemaSmartSearchV1Form } from '@/utils/schemaForms/schemaForms';
import { useScreenSize } from '@/utils/useScreenSize';
import { SelectedFilterChips } from './components';
import { SmartSearchV1FormProps } from './interfaces';
import { SmartSearchInputWrapper } from '../../Header';

export function SmartSearchV1Form({
  handleSubmit,
  clearSelectedSearchTypeFilter,
  selectedFilterChip = '',
  displayNoMatchError = false,
}: SmartSearchV1FormProps) {
  const theme = useTheme();
  const { isMobile } = useScreenSize();
  const { redirectToReportUrl } = useReportRedirect();
  const { logError, trackEventGA4 } = useAppConfigContext();

  const [currentInputValue, setCurrentInputValue] = useState('');
  const [searchType, setSearchType] = useState('empty');

  const rapidsStateRef = useRef<RapidsStateRef>(null);
  const inputMaxLength = 300;

  const {
    control,
    handleSubmit: handleSubmitForm,
    register,
    setValue,
    formState: { errors },
    trigger,
    clearErrors,
    setError,
  } = useForm({
    resolver: yupResolver(schemaSmartSearchV1Form[searchType]),
  });

  useEffect(() => {
    const noMatchErrMsg = "Sorry, we didn't find an exact match.";

    if (displayNoMatchError) {
      setError(searchType, {
        type: 'custom',
        message: noMatchErrMsg,
      });
    } else if (
      !displayNoMatchError &&
      errors &&
      errors[searchType] &&
      errors[searchType]?.message === noMatchErrMsg
    ) {
      clearErrors(searchType);
    }
  }, [clearErrors, displayNoMatchError, errors, searchType, setError]);

  useEffect(() => {
    const searchTypesByFilterMap: { [k: string]: string } = {
      People: 'person',
      Property: 'address',
      Phone: 'phone',
      Email: 'email',
      Username: 'username',
      Vehicle: 'vehicle',
    };

    if (selectedFilterChip && searchTypesByFilterMap[selectedFilterChip]) {
      setSearchType(searchTypesByFilterMap[selectedFilterChip]);
    } else {
      setSearchType('empty');
    }
  }, [searchType, selectedFilterChip]);

  const onSubmit = () => {
    if (searchType === 'empty') {
      if (isValueAPhoneNumber(currentInputValue)) {
        // Skip call to LLM-Service and go directly to report
        searchReportAndCloseModal('phone', {
          phone_number: unformatPhone(currentInputValue),
        });

        return;
      }

      handleSubmit({
        action: 'triggerAllReportTypesSmartSearch',
        searchValue: currentInputValue,
      });
    } else {
      // Run validation against the search type
      trigger(searchType)
        .then((isValid: boolean) => {
          if (isValid) {
            handleSmartSearch(currentInputValue);
          } else {
            logError(
              `SmartSearchV1Form: Validation for type ${searchType} is not valid`,
              new Error(
                `form validation failed for search type ${searchType} and value ${currentInputValue}`
              )
            );
          }
        })
        .catch((err) => {
          logError(
            'SmartSearchV1Form: Unable to trigger form validations',
            err
          );
        });
    }
  };

  const onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setValue(searchType, value);
    setCurrentInputValue(value);
  };

  const handleRapidsSearch = (rapidsData: RapidsSearchData) => {
    if (rapidsData.type === 'name') {
      const { fn, ln } = rapidsData;
      handleSubmit({
        action: 'suggestions',
        values: { firstName: fn, lastName: ln },
      });
      return;
    }

    const { city, state, zipcode, street_line } = rapidsData;
    const possibleParams = {
      street_line,
      city,
      state,
      zipcode,
    };
    handleSubmit({ action: 'suggestions', values: possibleParams });
  };

  const searchReportAndCloseModal = (
    searchReportType: ValidReportTypes,
    metaObj: object,
    reportFlags?: string[]
  ) => {
    const reportTypeMap: { [type: string]: RawReportType } = {
      username: 'username_report',
      email: 'social_network_report',
      phone: 'reverse_phone_report',
      property: 'property_report',
    };

    const reportType = reportTypeMap[searchReportType];
    if (!reportType) {
      return;
    }

    const body = {
      report_type: reportType,
      meta: {
        ...metaObj,
        report_flags: reportFlags || [],
      },
    } as ReportSearchRequestBody;

    const searchByValueByReportType: { [key: string]: string } = {
      username: 'username',
      email: 'email',
      phone: 'phone',
      property: 'address',
    };

    searchReport(body).then((response) => {
      if (response) {
        trackEventGA4({
          event_name: 'report_search',
          search_from: 'header_smart_search',
          report_type: searchReportType,
          search_by: searchByValueByReportType[searchReportType] || '',
        });
        redirectToReportUrl(searchReportType, response?.report?.permalink);
      }
    });

    handleSubmit({ action: 'closeModal' });
  };

  const handleSmartSearch = async (optionString: string) => {
    setCurrentInputValue(optionString);

    if (!selectedFilterChip) {
      handleSubmit({
        action: 'triggerAllReportTypesSmartSearch',
        searchValue: optionString,
      });
      return;
    }

    if (selectedFilterChip === 'People') {
      // Run LLM-Service search using People prompt
      handleSubmit({
        action: 'triggerPeopleSmartSearch',
        searchValue: optionString,
      });
      return;
    }

    if (selectedFilterChip === 'Property') {
      searchReportAndCloseModal('property', { address: optionString });
      return;
    }

    if (selectedFilterChip === 'Phone') {
      searchReportAndCloseModal('phone', {
        phone_number: unformatPhone(optionString),
      });
      return;
    }

    if (selectedFilterChip === 'Email') {
      searchReportAndCloseModal('email', { email: optionString });
      return;
    }

    if (selectedFilterChip === 'Username') {
      searchReportAndCloseModal('username', { username: optionString });
      return;
    }

    if (selectedFilterChip === 'Vehicle') {
      // Run LLM-Service search using Vehicle prompt
      handleSubmit({
        action: 'triggerVehicleSmartSearch',
        searchValue: optionString,
      });
    }
  };

  const placeholderByFilter = (filter: string): string | undefined => {
    if (!filter) {
      return 'Try searching a name, phone number, address, email, vehicle VIN or license plate & state';
    }

    const placeholderMap: { [key: string]: string } = {
      People: 'Try searching a name, age, location, death date, or occupation',
      Property: 'Try searching an address or other location',
      Phone: 'Try searching a valid phone number',
      Email: 'Try searching a valid email address',
      Username: 'Try searching a social media username',
      Vehicle:
        'Try searching by VIN number, license plate + state, or yr/make/model',
    };

    return placeholderMap[filter];
  };

  const adornmentInputProps = (): Partial<InputProps['InputProps']> => {
    return {
      startAdornment: (
        <InputAdornment
          position="start"
          sx={{
            margin: 0,
            color: theme.palette.primary.main,
          }}
        >
          <Icons.AutoAwesome sx={{ fontSize: '16px' }} />
          {selectedFilterChip && (
            <SelectedFilterChips className="selected-filter-chip">
              {selectedFilterChip}
              <IconButton
                onClick={() => {
                  if (clearSelectedSearchTypeFilter) {
                    clearSelectedSearchTypeFilter();
                  }
                  clearErrors();
                }}
              >
                <Icons.CloseRounded />
              </IconButton>
            </SelectedFilterChips>
          )}
        </InputAdornment>
      ),
      endAdornment: (
        <InputAdornment
          position="end"
          sx={{
            margin: 0,
            backgroundColor: `${theme.palette.success.dark} !important`,
            borderRadius: '100px',
            width: '26px !important',
            height: '26px !important',
            justifyContent: 'center',
          }}
        >
          <Icons.Search
            sx={{
              verticalAlign: 'bottom',
              fontSize: '1rem',
              width: '1.5rem',
              color: theme.palette.primary.contrastText,
              cursor: 'pointer',
            }}
            onClick={handleSubmitForm(onSubmit)}
          />
        </InputAdornment>
      ),
    };
  };

  const customRapidsInputStyles = {
    '.MuiButton-root': {
      margin: isMobile ? theme.spacing(2, 0, 0, 0) : theme.spacing(0, 0, 0, 2),
    },

    '.MuiAutocomplete-root label': {
      marginBottom: '0 !important',
    },

    width: '100%',
    borderRadius: '50px',

    '& .MuiAutocomplete-root': {
      width: '100%',
      borderRadius: '50px',
      backgroundColor: theme.palette.background.paper,

      '& .address-input-container': {
        '& .address-input': {
          '& .MuiTextField-root': {
            '& .MuiOutlinedInput-root': {
              height: '52px',
              borderRadius: '50px',
              backgroundColor: theme.palette.background.paper,
            },
          },
          '& .rapids-search-icon': {
            display: 'none',
          },
        },
      },
    },

    '& .rapids-input-errors': {
      display: 'none',
    },

    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },

    '&:hover': {
      cursor: 'pointer',
    },
  };

  return (
    <>
      <SmartSearchInputWrapper
        sx={{
          alignSelf: 'start',
          width: 'calc(100% - 14px)',
        }}
        className={errors && errors[searchType]?.message ? 'error' : ''}
      >
        <form onSubmit={handleSubmitForm(onSubmit)}>
          {searchType === 'address' ||
          searchType === 'person' ||
          searchType === 'empty' ? (
            <RapidsInput
              searchTypes={searchType === 'person' ? ['names'] : []}
              parentFormSubmitHandler={handleRapidsSearch}
              parentOptionStringSubmitHandler={handleSmartSearch}
              customActionOnEnter={handleSmartSearch}
              valueOveride={currentInputValue}
              updateEmptyOptions
              rapidsStateRef={rapidsStateRef}
              disableLoading
              inputOptions={{
                customStyles: customRapidsInputStyles,
                hideInputLabel: true,
                hideSearchButton: true,
                placeholder: placeholderByFilter(selectedFilterChip),
                inputWidth: {
                  md: '100%',
                },
                InputProps: adornmentInputProps(),
              }}
            />
          ) : (
            <Controller
              name={searchType}
              control={control}
              render={({ field: { value, onChange: onChangeField } }) => (
                <Input
                  autoFocus
                  inputRef={(input) => input?.focus()}
                  placeholder={placeholderByFilter(selectedFilterChip)}
                  sx={{
                    width: '100%',
                    height: '52px',
                    borderRadius: '50px',
                    backgroundColor: theme.palette.background.paper,
                  }}
                  InputProps={{
                    sx: {
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      height: '52px !important',
                      borderRadius: '50px',
                    },
                    ...adornmentInputProps(),
                  }}
                  // eslint-disable-next-line react/jsx-no-duplicate-props
                  inputProps={{ maxLength: inputMaxLength }}
                  {...register(searchType)}
                  onInput={onInput}
                  value={value}
                  type={searchType === 'phone' ? 'tel' : 'text'}
                  onChange={(e) => {
                    if (searchType === 'phone') {
                      const input = e.target.value;

                      if (!currentInputValue && input.length) {
                        onChangeField(formatPhoneInput(input));
                        setCurrentInputValue(formatPhoneInput(input));
                      } else if (input.length < currentInputValue.length) {
                        onChangeField(input);
                        setCurrentInputValue(input);
                      } else {
                        onChangeField(formatPhoneInput(input));
                        setCurrentInputValue(formatPhoneInput(input));
                      }
                    }
                  }}
                />
              )}
            />
          )}
        </form>
      </SmartSearchInputWrapper>
      {errors && errors[searchType] && (
        <Text color="error" alignSelf="baseline" data-cy="smart_search_error">
          <Icons.Warning
            fontSize="small"
            sx={{ verticalAlign: 'bottom', mr: 0.5 }}
          />
          {errors[searchType]?.message as string}
        </Text>
      )}
    </>
  );
}
